import type { MediaEntityInfo } from "@/types/api";
import type { ProjectDetailsScene, ProjectDetailsStoryboardShot, ProjectDetailsVisual } from "@/types/project";
import type { ClipTrimRange } from "@/types/timeline";
import { STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD } from "@/utils/constants";
import { coalesceNonEmptyStringArrays, moveImageIdsToFront } from "@/utils/utils";
import { userVideoGenPromptFromStoredPrompt } from "@/services/generation-prompts";
import { isOmniFamilyVideoModel } from "@/utils/videoExtend";
import { sceneTitleForShotSceneId, shotNumberFromShotId } from "@/services/storyboardShotIdentity";
import { patchStoryboardShotImagesForMainSlotChange, storyboardWorkingStillImageId } from "@/services/storyboardDisplayedImage";

/** Persisted trim window for `videoId`'s `visuals[]` row on `shot`, if both bounds are set. */
export function persistedVisualTrimRange(shot: ProjectDetailsStoryboardShot | undefined, videoId: string): ClipTrimRange | null {
  const visual = (shot?.visuals ?? []).find((v) => v.video_id === videoId);
  const startSec = visual?.trim_start_sec;
  const endSec = visual?.trim_end_sec;
  if (typeof startSec !== "number" || typeof endSec !== "number" || !Number.isFinite(startSec) || !Number.isFinite(endSec)) {
    return null;
  }

  return { startSec, endSec };
}

/** Find the shot owning `videoId`'s `visuals[]` row, if any. */
export function findStoryboardShotForVideoId(
  shots: readonly ProjectDetailsStoryboardShot[] | undefined,
  videoId: string
): ProjectDetailsStoryboardShot | null {
  return shots?.find((shot) => (shot.visuals ?? []).some((v) => v.video_id === videoId)) ?? null;
}

/** Set `trim_start_sec`/`trim_end_sec` on `videoId`'s `visuals[]` row; other rows untouched. */
export function withVisualTrimRange(shot: ProjectDetailsStoryboardShot, videoId: string, range: ClipTrimRange): ProjectDetailsStoryboardShot {
  const visuals = (shot.visuals ?? []).map((v) => (v.video_id === videoId ? { ...v, trim_start_sec: range.startSec, trim_end_sec: range.endSec } : v));

  return { ...shot, visuals };
}

/** Append a completed video visual to a shot; skips duplicate `video_id`. */
export function appendVisualToStoryboardShot(shot: ProjectDetailsStoryboardShot, visual: ProjectDetailsVisual): ProjectDetailsStoryboardShot {
  const existing = shot.visuals ?? [];
  if (existing.some((v) => v.video_id === visual.video_id)) {
    return shot;
  }

  return { ...shot, visuals: [...existing, visual] };
}

/** Append visual; order `images` as `[video_id, reference_image_id?, …]` via {@link moveImageIdsToFront}. */
export function appendVisualToStoryboardShotPrependingPrimaryVideo(
  shot: ProjectDetailsStoryboardShot,
  visual: ProjectDetailsVisual
): ProjectDetailsStoryboardShot {
  const existing = shot.visuals ?? [];
  const isDuplicate = existing.some((v) => v.video_id === visual.video_id);
  const withVisual = appendVisualToStoryboardShot(shot, visual);

  if (isDuplicate) {
    return withVisual;
  }

  const videoId = visual.video_id;
  const refId = visual.reference_image_id;
  const hasVideo = typeof videoId === "string" && videoId !== "";
  const hasRef = typeof refId === "string" && refId !== "";

  if (!hasVideo && !hasRef) {
    return withVisual;
  }

  const prev = withVisual.images ?? [];
  let orderedFront: string[];
  if (hasRef && hasVideo) {
    // For AI video generations we want `images[0]` to remain the still reference image, not the video asset id.
    orderedFront = [refId, videoId];
  } else if (hasRef) {
    orderedFront = [refId];
  } else {
    orderedFront = [videoId];
  }

  const nextImages = moveImageIdsToFront(prev, orderedFront);

  return { ...withVisual, images: nextImages };
}

/** Merge query/store/Run shot slices, append completed visual, apply video/ref image ordering. */
export function mergeStoryboardShotForCompletedVideo(
  queryShot: ProjectDetailsStoryboardShot,
  storeShot: ProjectDetailsStoryboardShot | undefined,
  shotAtRun: ProjectDetailsStoryboardShot,
  visual: ProjectDetailsVisual
): ProjectDetailsStoryboardShot {
  const images = [...coalesceNonEmptyStringArrays(queryShot.images, storeShot?.images, shotAtRun.images)];
  const approvedTimeline = [
    ...coalesceNonEmptyStringArrays(
      queryShot["approved-timeline-images"],
      storeShot?.["approved-timeline-images"],
      shotAtRun["approved-timeline-images"]
    )
  ];
  const videoUploadedImages = [
    ...new Set(
      [
        ...(queryShot["video-uploaded-images"] ?? []),
        ...(storeShot?.["video-uploaded-images"] ?? []),
        ...(shotAtRun["video-uploaded-images"] ?? [])
      ].filter((id) => id.trim() !== "")
    )
  ];

  /** Same reason as the arrays above: the store can hold an approval the server copy has not caught up with. */
  const approvedVideoId = [queryShot["approved-video-id"], storeShot?.["approved-video-id"], shotAtRun["approved-video-id"]].find(
    (id) => typeof id === "string" && id.trim() !== ""
  );

  const merged: ProjectDetailsStoryboardShot = {
    ...queryShot,
    images,
    "approved-timeline-images": approvedTimeline,
    "video-uploaded-images": videoUploadedImages,
    ...(approvedVideoId === undefined ? {} : { "approved-video-id": approvedVideoId })
  };

  const withVisual = appendVisualToStoryboardShotPrependingPrimaryVideo(merged, visual);
  const refId = visual.reference_image_id;
  const vidId = visual.video_id;
  if (typeof refId === "string" && refId !== "") {
    return { ...withVisual, images: moveImageIdsToFront(withVisual.images, [refId, vidId]) };
  }

  return withVisual;
}

/** Duplicate-shot: retarget `visuals` metadata to the new composite shot id. */
export function cloneVisualsForDuplicatedStoryboardShot(
  visuals: readonly ProjectDetailsVisual[] | undefined,
  newStoryboardShotCompositeId: string,
  newShotIdPart: string
): ProjectDetailsVisual[] {
  const shotNumber = shotNumberFromShotId(newShotIdPart);

  return (visuals ?? []).map((v) => ({
    ...v,
    storyboard_shot_id: newStoryboardShotCompositeId,
    "shot-number": shotNumber
  }));
}

/**
 * Find a completed storyboard `visuals[]` row by `video_id` anywhere in the project.
 * When `referenceImageId` is set, require the same `reference_image_id` on that row.
 */
export function findCompletedStoryboardVisualByVideoId(
  shots: readonly ProjectDetailsStoryboardShot[] | undefined,
  videoId: string,
  referenceImageId?: string | null
): ProjectDetailsVisual | null {
  if (!shots?.length || typeof videoId !== "string" || videoId === "") {
    return null;
  }

  const wantRef =
    typeof referenceImageId === "string" && referenceImageId.trim() !== "" ? referenceImageId.trim() : null;

  for (const shot of shots) {
    for (const raw of shot.visuals ?? []) {
      const v = raw as ProjectDetailsVisual;
      if (v.status !== "completed") {
        continue;
      }

      if (v.video_id !== videoId) {
        continue;
      }

      if (wantRef !== null && v.reference_image_id !== wantRef) {
        continue;
      }

      return v;
    }
  }

  return null;
}

/** Library drag of a video with no `visuals[]` row anywhere (orphan generation): build one from its own S3 metadata so it can be attached like any other completed video. */
export function synthesizeCompletedStoryboardVisualFromMediaEntity(
  videoId: string,
  info: MediaEntityInfo | undefined,
  fallbackReferenceImageId: string
): ProjectDetailsVisual {
  const parentIds = info?.parent_ids ?? info?.parentIds ?? [];
  const referenceImageId = parentIds[0]?.trim() || fallbackReferenceImageId;
  const nowIso = new Date().toISOString();
  const createdAt = typeof info?.created_date === "string" && info.created_date !== "" ? info.created_date : nowIso;

  return {
    video_id: videoId,
    ...(typeof info?.model === "string" && info.model !== "" ? { model: info.model } : {}),
    duration: typeof info?.duration === "number" ? info.duration : 8,
    prompt: info?.prompt ?? "",
    reference_image_id: referenceImageId,
    ...(typeof info?.poster_image_id === "string" && info.poster_image_id !== ""
      ? { poster_image_id: info.poster_image_id }
      : {}),
    status: "completed",
    created_at: createdAt,
    completed_at: createdAt,
    error_message: null
  };
}

/**
 * Library drag: prefer a completed visual whose `reference_image_id` matches the card still,
 * but fall back to any completed row with the same `video_id` when the poster id differs.
 */
export function findCompletedStoryboardVisualForLibraryDrag(
  shots: readonly ProjectDetailsStoryboardShot[] | undefined,
  videoId: string,
  referenceImageId?: string | null
): ProjectDetailsVisual | null {
  const strict = findCompletedStoryboardVisualByVideoId(shots, videoId, referenceImageId);
  if (strict !== null) {
    return strict;
  }

  return findCompletedStoryboardVisualByVideoId(shots, videoId, null);
}

function copiedVideoGenPromptFromLibrarySource(
  sourceShot: ProjectDetailsStoryboardShot | undefined,
  sourceVisual: ProjectDetailsVisual,
  targetShot: ProjectDetailsStoryboardShot
): string {
  const fromShot = sourceShot?.["video-gen-prompt"]?.trim() ?? "";
  if (fromShot.length > 0) {
    return fromShot;
  }

  const fromVisual = userVideoGenPromptFromStoredPrompt(sourceVisual.prompt);
  if (fromVisual.length > 0) {
    return fromVisual;
  }

  return targetShot["video-gen-prompt"] ?? "";
}

function copiedVideoUploadedImagesFromLibrarySource(
  sourceShot: ProjectDetailsStoryboardShot | undefined,
  sourceVisual: ProjectDetailsVisual,
  targetShot: ProjectDetailsStoryboardShot,
  refId: string
): readonly string[] {
  const sourceUploaded = sourceShot?.["video-uploaded-images"] ?? [];
  const inheritable = isOmniFamilyVideoModel(sourceVisual.model)
    ? sourceUploaded
    : sourceUploaded.filter((id) => id !== storyboardWorkingStillImageId(sourceShot));
  if (inheritable.length > 0) {
    return [...inheritable];
  }

  if (isOmniFamilyVideoModel(sourceVisual.model)) {
    return targetShot["video-uploaded-images"] ?? [];
  }

  return refId === "" ? (targetShot["video-uploaded-images"] ?? []) : [refId];
}

/** Clone a `visuals` row for another shot: update ids used in stored metadata. */
export function retargetStoryboardVisualToShot(
  visual: ProjectDetailsVisual,
  targetShot: ProjectDetailsStoryboardShot,
  scenes: readonly ProjectDetailsScene[] | undefined
): ProjectDetailsVisual {
  const sceneName = sceneTitleForShotSceneId(scenes, targetShot["scene-id"]);
  return {
    ...visual,
    storyboard_shot_id: targetShot.id,
    "shot-number": shotNumberFromShotId(targetShot["shot-id"]),
    ...(sceneName !== "" ? { "scene-name": sceneName } : {})
  };
}

/**
 * Copy a completed `visuals` row from elsewhere in the project onto `targetShot` and apply primary-slot
 * `images` ordering. Optionally approve the copied video (Timeline slot drops only).
 */
export function storyboardShotWithCopiedCompletedVideo(
  targetShot: ProjectDetailsStoryboardShot,
  sourceVisual: ProjectDetailsVisual,
  scenes: readonly ProjectDetailsScene[] | undefined,
  options: {
    readonly approveCopiedVideo: boolean;
    /** Library drag image id when `sourceVisual.reference_image_id` is absent. */
    readonly fallbackReferenceImageId: string;
    /** Shot the dragged video was originally generated on; used to restore its prompt and dialogues (not stored on the visual). */
    readonly sourceShot?: ProjectDetailsStoryboardShot;
  }
): ProjectDetailsStoryboardShot {
  const retargeted = retargetStoryboardVisualToShot(sourceVisual, targetShot, scenes);
  const vid = retargeted.video_id;
  if (typeof vid !== "string" || vid === "") {
    return targetShot;
  }

  const refId =
    typeof retargeted.reference_image_id === "string" && retargeted.reference_image_id !== ""
      ? retargeted.reference_image_id
      : options.fallbackReferenceImageId;

  const hadVideoOnTarget = (targetShot.visuals ?? []).some((v) => v.video_id === vid);
  let next = appendVisualToStoryboardShotPrependingPrimaryVideo(targetShot, retargeted);

  if (hadVideoOnTarget) {
    next = patchStoryboardShotImagesForMainSlotChange(next, moveImageIdsToFront(next.images ?? [], [refId, vid]));
  }

  if (options.approveCopiedVideo) {
    next = { ...next, "approved-video-id": vid };
  }

  const nextVideoUploadedImages = copiedVideoUploadedImagesFromLibrarySource(
    options.sourceShot,
    retargeted,
    next,
    refId
  );

  next = {
    ...next,
    "video-gen-prompt": copiedVideoGenPromptFromLibrarySource(options.sourceShot, retargeted, next),
    dialogues: options.sourceShot?.dialogues ?? next.dialogues,
    "video-uploaded-images": [...nextVideoUploadedImages]
  };

  return next;
}

type TransactionPayload = Record<string, unknown>;

/** Build the `visuals[]` row persisted on a storyboard shot after a successful video transaction (see `VideoRequest.to_dict`). */
export function buildCompletedStoryboardVideoVisual(args: {
  readonly videoId: string;
  readonly transactionData: TransactionPayload | undefined;
  readonly referenceImageId: string;
  readonly prompt: string;
  readonly duration: number;
  readonly videoModel: string;
  readonly sceneName: string;
  readonly shotNumber: number;
  readonly storyboardShotId: string;
}): ProjectDetailsVisual {
  const p = args.transactionData ?? {};
  const nowIso = new Date().toISOString();
  const serverRequestId = p["server_request_id"];
  const apiEndpoint = p["api_endpoint"];
  const createdAt = p["created_at"];
  const completedAt = p["completed_at"];

  return {
    video_id: args.videoId,
    ...(typeof serverRequestId === "string" ? { server_request_id: serverRequestId } : {}),
    ...(typeof apiEndpoint === "string" && apiEndpoint !== "" ? { api_endpoint: apiEndpoint } : {}),
    model: args.videoModel,
    duration: args.duration,
    prompt: args.prompt,
    reference_image_id: args.referenceImageId,
    status: "completed",
    "scene-name": args.sceneName,
    "shot-number": args.shotNumber,
    storyboard_shot_id: args.storyboardShotId,
    created_at: typeof createdAt === "string" && createdAt.length > 0 ? createdAt : nowIso,
    completed_at: typeof completedAt === "string" && completedAt.length > 0 ? completedAt : nowIso,
    error_message: null
  };
}

/** User upload: order `[posterImageId, videoId, …]`; set visual `reference_image_id` to poster. */
export function patchStoryboardShotAppendPosterForPrimaryUploadedVideo(
  shot: ProjectDetailsStoryboardShot,
  videoId: string,
  posterImageId: string
): ProjectDetailsStoryboardShot {
  const nextImages = moveImageIdsToFront(shot.images, [posterImageId, videoId]);

  const visuals = (shot.visuals ?? []).map((v) => {
    if (
      v.status === "completed" &&
      v.video_id === videoId &&
      v.model === STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD &&
      (v.reference_image_id === videoId || v.reference_image_id === posterImageId)
    ) {
      return { ...v, reference_image_id: posterImageId, poster_image_id: posterImageId };
    }

    return v;
  });

  return { ...shot, images: nextImages, visuals };
}

/** AI video: upload poster PNG, move poster to front; set visual `reference_image_id` to poster. */
export function patchStoryboardShotAppendPosterForGeneratedVideo(
  shot: ProjectDetailsStoryboardShot,
  videoId: string,
  posterImageId: string
): ProjectDetailsStoryboardShot {
  const nextImages = moveImageIdsToFront(shot.images, [posterImageId, videoId]);

  const visuals = (shot.visuals ?? []).map((v) => {
    if (
      v.status === "completed" &&
      v.video_id === videoId &&
      v.model !== STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD
    ) {
      return { ...v, reference_image_id: posterImageId, poster_image_id: posterImageId };
    }

    return v;
  });

  return { ...shot, images: nextImages, visuals };
}

/**
 * After uploading a poster PNG for a generated video, enforce **`images[0]` = poster**, **`images[1]` = video**
 * (same primary-slot ordering as storyboard video upload in {@link PageContent}).
 */
export function storyboardShotWithPosterVideoPrimaryOrder(
  shot: ProjectDetailsStoryboardShot,
  posterImageId: string,
  videoId: string
): ProjectDetailsStoryboardShot {
  return {
    ...shot,
    images: moveImageIdsToFront(shot.images, [posterImageId, videoId])
  };
}

/** Completed visual row for MainSpace video upload. Reference still: `images[0]` when not the video id, else `images[1]` (legacy), else video id. */
export function buildUserUploadedVideoVisual(args: {
  readonly videoId: string;
  readonly filename: string;
  readonly shot: ProjectDetailsStoryboardShot;
  readonly sceneName: string;
}): ProjectDetailsVisual {
  const imgs = args.shot.images ?? [];
  const vid = args.videoId;
  const first = imgs[0];
  const second = imgs[1];
  let referenceImageId: string;
  if (typeof first === "string" && first !== "" && first !== vid) {
    referenceImageId = first;
  } else if (typeof second === "string" && second !== "" && second !== vid) {
    referenceImageId = second;
  } else {
    referenceImageId = vid;
  }

  const durationRaw = args.shot["assumed-video-duration"];
  const duration = typeof durationRaw === "number" && Number.isFinite(durationRaw) && durationRaw > 0 ? durationRaw : 8;

  return buildCompletedStoryboardVideoVisual({
    videoId: args.videoId,
    transactionData: undefined,
    referenceImageId,
    prompt: `Uploaded: ${args.filename}`,
    duration,
    videoModel: STORYBOARD_VIDEO_MODEL_USER_FILE_UPLOAD,
    sceneName: args.sceneName,
    shotNumber: shotNumberFromShotId(args.shot["shot-id"]),
    storyboardShotId: args.shot.id
  });
}
